import { t } from "i18next";

import React from "react";

import { KcalBadge } from "@components/OrderTaking/Components/common/KcalBadge";
import type { Composition } from "@components/OrderTaking/Helpers/calories";
import { resolveKcal } from "@components/OrderTaking/Helpers/calories";

type ShopCartCompositions = Record<
    string,
    { compositions?: Record<string, Composition & { displayTitle?: string }> }
>;

type MenuCompositionsLabelsType =
    | { compositions: ShopCartCompositions; shopCart: true }
    | { compositions: string[]; shopCart: false };

type Entry = {
    title: string;
    kcal: number;
};

export function MenuCompositionsLabels({
    compositions,
    shopCart,
}: MenuCompositionsLabelsType): JSX.Element {
    const entries: Entry[] = [];

    if (shopCart) {
        const shopCartCompositions = compositions as ShopCartCompositions;
        Object.keys(shopCartCompositions).forEach((key) => {
            const stepCompositions = shopCartCompositions[key]?.compositions;
            if (stepCompositions && Object.keys(stepCompositions).length > 0) {
                Object.values(stepCompositions).forEach((product) => {
                    entries.push({
                        title: String(product.displayTitle || "").toLowerCase(),
                        kcal: resolveKcal(product),
                    });
                });
            }
        });
    } else {
        (compositions as string[]).forEach((title) => {
            entries.push({ title, kcal: 0 });
        });
    }

    // Group identical titles, summing kcal contributions per occurrence.
    const grouped = new Map<string, { count: number; kcal: number }>();
    entries.forEach(({ title, kcal }) => {
        const existing = grouped.get(title);
        if (existing) {
            existing.count += 1;
            existing.kcal += kcal;
        } else {
            grouped.set(title, { count: 1, kcal });
        }
    });

    const items = Array.from(grouped.entries());

    return (
        <div className="d-flex flex-column justify-content-start align-items-start">
            <span
                style={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    color: "#747272",
                    textAlign: "left",
                }}
            >
                {t("You have selected:")}
            </span>
            <div
                className="summary-product-compositions"
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    rowGap: 6,
                    alignItems: "center",
                }}
            >
                {items.map(([title, { count, kcal }], index) => {
                    const prefix = count > 1 ? `${count}x ` : "";
                    const isLast = index === items.length - 1;
                    return (
                        <span
                            key={title}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            <span>{`${prefix}${title}${
                                isLast ? "." : ","
                            }`}</span>
                            {kcal > 0 ? (
                                <KcalBadge kcal={kcal} size="sm" />
                            ) : null}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}
